import { extractErrorMessage } from "@/lib/error"
import { clearUserSessionStores } from "@/lib/store-utils"
import { resetTracking } from "@/lib/tracking/analytics"
import { api } from "@/services/api"
import { authStore, clearAuthStore } from "@/store/auth"

import { authService } from "../services/authService"
import { LoginFormValues, User } from "../types"

export async function login(credentials: LoginFormValues, recaptchaToken: string): Promise<User> {
  authStore.setState({ isLoading: true, error: null })

  try {
    // Pass the token to the service
    const response = await authService.login(credentials, recaptchaToken)

    authStore.setState({
      user: response.user,
      token: response.access_token,
      refreshToken: response.refresh_token,
      tokenType: response.token_type,
      isLoading: false,
      isAuthenticated: true,
    })

    // Limpar todos os storages relacionados à sessão do usuário
    clearUserSessionStores()

    return response.user
  } catch (error) {
    const errorMessage = extractErrorMessage(error, "Erro ao fazer login")
    authStore.setState({
      error: errorMessage,
      isLoading: false,
      isAuthenticated: false,
    })
    throw error
  }
}

export async function forgotPassword(email: string): Promise<void> {
  authStore.setState({ isLoading: true, error: null })

  try {
    await authService.forgotPassword(email)
    authStore.setState({ isLoading: false })
  } catch (error) {
    console.error(error)
    const errorMessage = extractErrorMessage(error, "Erro ao solicitar redefinição de senha")
    authStore.setState({
      error: errorMessage,
      isLoading: false,
    })
    throw error
  }
}

export function logout(): void {
  resetTracking()

  clearUserSessionStores()
  clearAuthStore()
}

export function clearError(): void {
  authStore.setState({ error: null })
}

export async function refreshToken(): Promise<boolean> {
  try {
    const response = await authService.refreshToken(authStore.getState().refreshToken!)

    // Atualiza o estado de autenticação com o novo token
    authStore.setState({
      user: response.user,
      token: response.access_token,
      tokenType: response.token_type,
      isAuthenticated: true,
    })

    return true
  } catch (error) {
    console.error("Erro ao atualizar token:", error)
    return false
  }
}

export async function getUserInfo(): Promise<User> {
  const response = await authService.getUserInfo()
  authStore.setState({ user: response })
  return response
}

export async function markUserAsOnboardingCompleted(): Promise<void> {
  const response = await authService.markUserAsOnboardingCompleted(authStore.getState().user!.id)
  return response
}

export const verifyEmailTokenAction = async (refreshToken: string): Promise<User> => {
  try {
    const response = await authService.refreshToken(refreshToken)

    // Directly update the store state
    authStore.setState({
      token: response.access_token,
      refreshToken: response.refresh_token,
      user: response.user, // Cast if necessary, ensure User type is imported
      isAuthenticated: true,
      isLoading: false,
      error: null,
      // tokenType might also be in the response, add if needed: tokenType: response.token_type
    })

    return response.user
  } catch (error) {
    console.error("Error verifying email token action:", error)
    authStore.setState({ isLoading: false, error: "Falha ao verificar o token de e-mail." })
    // Re-throw the error to be handled by the calling component
    throw new Error("Falha ao verificar o token de e-mail.")
  }
}

export const handleGoogleCallbackAction = async (searchParams: URLSearchParams): Promise<User> => {
  authStore.setState({ isLoading: true, error: null })
  const code = searchParams.get("code")

  if (!code) {
    const errorMsg = "Google OAuth callback error: Código não encontrado."
    authStore.setState({ error: errorMsg, isLoading: false })
    throw new Error(errorMsg) // Throw error to be caught by the component
  }

  try {
    const callbackUrl = `/auth/google/callback?${searchParams.toString()}`
    const response = await api.get(callbackUrl)

    const { user, access_token, refresh_token, token_type } = response.data as {
      user: User
      access_token: string
      refresh_token?: string
      token_type?: string
    }

    if (user && access_token) {
      authStore.setState({
        user,
        token: access_token,
        refreshToken: refresh_token ?? null,
        tokenType: token_type ?? null,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      })
      clearUserSessionStores() // Clear session stores on successful login
      console.log("Google OAuth successful via action.")
      return user // Return user data on success
    } else {
      const errorMsg = "Google OAuth callback error: Resposta inválida do backend."
      authStore.setState({ error: errorMsg, isLoading: false })
      throw new Error(errorMsg)
    }
  } catch (error) {
    const errorMsg =
      error instanceof Error ? error.message : "Erro desconhecido ao fazer login com Google"
    authStore.setState({ error: errorMsg, isLoading: false })
    throw new Error(errorMsg) // Re-throw error - tracked in GoogleCallbackRoute
  }
}

export const handleFacebookCallbackAction = async (
  searchParams: URLSearchParams
): Promise<User> => {
  authStore.setState({ isLoading: true, error: null })
  const code = searchParams.get("code")

  if (!code) {
    const errorMsg = "Facebook OAuth callback error: Codigo nao encontrado."
    console.error(errorMsg)
    authStore.setState({ error: errorMsg, isLoading: false })
    throw new Error(errorMsg)
  }

  try {
    const callbackUrl = `/auth/facebook/callback?${searchParams.toString()}`
    const response = await api.get(callbackUrl)

    const { user, access_token, refresh_token, token_type } = response.data as {
      user: User
      access_token: string
      refresh_token?: string
      token_type?: string
    }

    if (user && access_token) {
      authStore.setState({
        user,
        token: access_token,
        refreshToken: refresh_token ?? null,
        tokenType: token_type ?? null,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      })
      clearUserSessionStores()
      return user
    } else {
      const errorMsg = "Facebook OAuth callback error: Resposta invalida do backend."
      console.error(errorMsg, response.data)
      authStore.setState({ error: errorMsg, isLoading: false })
      throw new Error(errorMsg)
    }
  } catch (error) {
    const errorMsg =
      error instanceof Error ? error.message : "Erro desconhecido ao fazer login com Facebook"
    console.error("Facebook OAuth callback falhou na acao:", error)
    authStore.setState({ error: errorMsg, isLoading: false })
    throw new Error(errorMsg)
  }
}
